import RauliLogo from "@app/components/RauliLogo";
import userStorage from "@app/storage/User";
import logger from "@app/utils/logger";
import { Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { ErrorInfo, ReactNode } from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";

export interface IRauliError extends WithTranslation {
  children: ReactNode;
  useMinimal?: boolean;
}

export type TErrorType = "network" | "chuckload" | null;

export interface IErrorState {
  hasError: boolean;
  errorType: TErrorType;
  errorText?: string;
}

class RauliError extends React.Component<IRauliError, IErrorState> {
  constructor(props: IRauliError) {
    super(props);
    this.state = {
      hasError: false,
      errorType: null,
      errorText: undefined,
    };
  }

  static getDerivedStateFromError(error: Error): IErrorState {
    let errorType: TErrorType = null;

    switch (error.name) {
      case "ChunkLoadError":
        errorType = "chuckload";
        break;
      case "NetworkError":
        errorType = "network";
        break;
      default:
        break;
    }

    return {
      hasError: true,
      errorType: errorType,
      errorText: typeof error === "string" ? error : undefined,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("Uncatched error!", error.name, errorInfo, error);
    if (!["ChunkLoadError", "NetworkError"].includes(error.name)) {
      Sentry.captureException(error, {
        extra: {
          errorInfo,
        },
      });
    }
  }

  render() {
    const { t, useMinimal } = this.props;

    if (this.state.hasError) {
      const errorContent = (
        <>
          <Typography>{t("Something went wrong!")}</Typography>
          {this.state.errorType && (
            <Typography>
              {this.state.errorType === "network" &&
                t(
                  "Page loading failed. Please check your connectivity and reload the page."
                )}
              {this.state.errorType === "chuckload" &&
                t(
                  "Page loading failed. Please check your connectivity and reload the page."
                ) +
                  " " +
                  t(
                    "The error code implies that the application might also have been updated, so approve the new version when prompted."
                  )}
            </Typography>
          )}
          <Typography>
            <Trans>
              Try{" "}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  window.location.reload();
                }}
              >
                reloading the page
              </a>{" "}
              or go back to the <a href="/">frontpage</a>.
            </Trans>
          </Typography>
          <Typography>
            <Trans>
              If nothing else helps, you can try to{" "}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  userStorage.logout(true).then(() => {
                    window.location.href = "/";
                  });
                }}
              >
                reset all settings to default values and logout
              </a>
              .
            </Trans>
          </Typography>
          {this.state.errorText && (
            <Typography variant="caption">
              {t("Error: {{ errorText }}", { errorText: this.state.errorText })}
            </Typography>
          )}
        </>
      );

      if (useMinimal) {
        return (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "100%",
              "& p": {
                lineHeight: 1,
              },
            }}
          >
            {errorContent}
          </Stack>
        );
      }

      return (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            width: "100%",
            paddingTop: "10%",
          }}
        >
          <RauliLogo withoutLink />
          {errorContent}
        </Stack>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(RauliError);
